import React, { ReactNode, useEffect, useMemo } from 'react';
import cl from 'classnames';
import { useContextData } from 'src/hooks';
import { uuidV4 } from 'src/helper';
import styles from './modalStyle.module.scss';

type ModalProps = {
  show: boolean;
  close: () => void;
  header: string;
  children: ReactNode;
  headerEl?: ReactNode;
  footer?: ReactNode;
  className?: string;
  disabledClose?: boolean;
  zIndex?: number;
  withShadow?: boolean;
  closeOnEsc?: boolean;
};

export const Modal = ({
  show,
  close,
  header,
  children,
  headerEl,
  footer,
  className,
  disabledClose = false,
  zIndex = 300,
  withShadow = false,
  closeOnEsc = false,
}: ModalProps) => {
  const {
    escHandler: {
      closeModalId,
      clearCloseModalId,
      addToModalList,
      removeFromModalList,
    },
  } = useContextData();
  const uuid = useMemo(uuidV4, []);

  useEffect(() => {
    if (!closeOnEsc) {
      return;
    }

    if (show) {
      addToModalList(uuid);
    } else {
      removeFromModalList(uuid);
    }
  }, [closeOnEsc, show, uuid, addToModalList, removeFromModalList]);

  useEffect(() => {
    if (!closeOnEsc) {
      return;
    }

    if (show && uuid === closeModalId) {
      clearCloseModalId();

      if (!disabledClose) {
        close();
      }
    }
  }, [
    clearCloseModalId,
    close,
    closeModalId,
    closeOnEsc,
    disabledClose,
    show,
    uuid,
  ]);

  return (
    <div className={cl(styles.modal, show && styles.show)} style={{ zIndex }}>
      <div className={styles.wrapper}>
        <div className={cl(styles.modalWindow, className)}>
          <div
            className={cl(styles.modalHeader, withShadow && styles.withShadow)}
          >
            <div
              className={styles.close}
              onClick={() => {
                if (disabledClose) {
                  return;
                }

                close();
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4966_54804)">
                  <path
                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                    fill={disabledClose ? '#EEE' : '#9EA6BD'}
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4966_54804">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            {header}
            {headerEl}
          </div>
          <div className={styles.modalContent}>{children}</div>
          {Boolean(footer) && (
            <div
              className={cl(
                styles.modalFooter,
                withShadow && styles.withShadow,
              )}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
